// ── ScopeAssignModal ───────────────────────────────────────────────────────────
// Opens in 'framework' or 'category' scope mode for L1/L2 assignments.
// Re-uses the same assignee email/UUID input pattern from AssignModal (D.1).

import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateScopeAssignment, useUpdateScopeAssignment, useClearScopeOverrides } from './scope-api';
import type { ScopeAssignment } from '@grc/shared';

interface ScopeAssignModalProps {
  frameworkId: string;
  /** 'framework' = L1, 'category' = L2 */
  scopeType: 'framework' | 'category';
  /** The category string for L2 scope; omit for L1 */
  category?: string;
  /** Existing scope row if editing/reassigning */
  existing?: ScopeAssignment;
  /** Count of L3 overrides in scope (for clear-overrides affordance) */
  overrideCount?: number;
  onClose: () => void;
}

export function ScopeAssignModal({
  frameworkId,
  scopeType,
  category,
  existing,
  overrideCount = 0,
  onClose,
}: ScopeAssignModalProps) {
  const [assigneeInput, setAssigneeInput] = useState(
    existing?.assignee_email ?? existing?.assignee_display_name ?? '',
  );
  const [verifierInput] = useState(existing?.verifier_email ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const createMutation = useCreateScopeAssignment(frameworkId);
  const updateMutation = useUpdateScopeAssignment(frameworkId);
  const clearMutation  = useClearScopeOverrides(frameworkId);

  const isEmailLike = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  function buildAssigneeFields(input: string) {
    if (isEmailLike(input)) return { assignee_email: input };
    return {};
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!assigneeInput.trim()) { toast.error('Assignee required'); return; }

    const assigneeFields = buildAssigneeFields(assigneeInput.trim());
    void verifierInput; // captured for future verifier support; not yet sent to API

    if (existing) {
      updateMutation.mutate(
        { id: existing.id, notes: notes || undefined, ...assigneeFields },
        {
          onSuccess: () => { toast.success('Scope assignment updated'); onClose(); },
          onError: () => toast.error('Update failed'),
        },
      );
    } else {
      createMutation.mutate(
        {
          framework_id: frameworkId,
          category: scopeType === 'category' ? category : undefined,
          notes: notes || undefined,
          ...assigneeFields,
        },
        {
          onSuccess: () => {
            toast.success(
              scopeType === 'framework'
                ? 'Framework-level assignment created'
                : `Category "${category}" assigned`,
            );
            onClose();
          },
          onError: () => toast.error('Assignment failed — a scope assignment may already exist for this scope'),
        },
      );
    }
  }

  function handleClearOverrides() {
    if (!existing) return;
    clearMutation.mutate(existing.id, {
      onSuccess: (r) => {
        toast.success(`Cleared ${r.cleared} control-level override${r.cleared !== 1 ? 's' : ''}`);
        setShowClearConfirm(false);
        onClose();
      },
      onError: () => toast.error('Clear overrides failed'),
    });
  }

  const isPending = createMutation.isPending || updateMutation.isPending || clearMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-xl">
        <h2 className="text-base font-semibold text-text-primary mb-4">
          {existing ? 'Reassign' : 'Assign'}{' '}
          {scopeType === 'framework' ? 'Framework' : `Category: ${category}`}
        </h2>

        {!showClearConfirm ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Assignee (email)</Label>
              <Input
                placeholder="user@example.com"
                value={assigneeInput}
                onChange={(e) => setAssigneeInput(e.target.value)}
                required
                className="text-sm"
              />
              <p className="text-[10px] text-text-secondary">
                Enter an email address. If they don't have a platform account, a pending-user record will be created.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Notes (optional)</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none resize-none"
                placeholder="Add context for this assignment"
              />
            </div>

            {existing && overrideCount > 0 && (
              <button
                type="button"
                onClick={() => setShowClearConfirm(true)}
                className="text-xs text-accent-cyan hover:underline"
              >
                {overrideCount} control{overrideCount !== 1 ? 's have' : ' has'} explicit overrides — clear them to apply this assignment everywhere
              </button>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isPending || !assigneeInput.trim()}>
                {existing ? 'Update' : 'Assign'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-text-primary">
              This will soft-delete <span className="font-semibold">{overrideCount} control-level assignment{overrideCount !== 1 ? 's' : ''}</span> within this scope. They will revert to inheriting from this {scopeType}-level assignment. This action is recorded in the audit log.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowClearConfirm(false)} disabled={isPending}>
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-accent-rose border-accent-rose/40 hover:bg-accent-rose/10"
                onClick={handleClearOverrides}
                disabled={isPending}
              >
                Clear {overrideCount} override{overrideCount !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
